// assistantDeck.js — PRESENTATION: a LIVE DECK in the main area
// (AI_ASSISTANT_SPEC v1.1 §12.1–§12.2; the ai.boards DECK contract v1 of the
// aib lane). Nika 2026-09-29: "dropdown should contain [… Presentation]";
// Presentation = "Live deck in thread".
//
// A deck is the ai.boards path's second shape: ONE fast model call, no tools —
// the same `generate` / `refine` ops with `mode: 'presentation'`. The model
// never writes a number: a slide is a headline (+ an optional subtitle and
// speaker notes) and one or two LIVE items — an installed app's widget with
// its params, or a declarative RECORDS BLOCK whose rows the renderer fetches
// through the SDK (`ai.boards.records`). This module turns the DeckSpec into
// what components/AssistantDeckView.js paints:
//
//   assistantDeckSlidesFor   DeckSpec × the installed vocabulary → the slides;
//                            each content slide's items go through the BOARD's
//                            own derivation (boardItemsFor → validateBoardSpec),
//                            so a slide mounts exactly what a board would, and
//                            an item this shell cannot mount is counted, never
//                            guessed at
//   applyDeckFrame           the streaming reducer over the board.* frames
//                            (board.start · board.head · board.slide … ·
//                            board.drop · board.done (authoritative) ·
//                            board.error)
//   assistantDeckGenerate    a send in Presentation mode (functions/homeAiSend.js)
//                            — a follow-up while a deck shows REFINES it
//   assistantDeckGo / Present / Save / Dismiss / Retry — the view's verbs
//
// THE VIEW MODEL (`root._aiDeck`, workspace-scoped, beside `_aiBoard`):
//   { v, status: 'loading'|'streaming'|'ready'|'failed', requestId,
//     workspaceId, path, prompt, op, raw, id, title, summary, slides, index,
//     total, dropped, error, save, savedId, notice, dismissed, explicit,
//     preview, startedAt, receivedAt }
// `path` is the route the deck was made on: the deck shows in THAT page's main
// area (the home's grid area on the home, the page layer elsewhere). ONE view
// holds the main area — the main thread and a generated board step aside for a
// deck, and a deck steps aside for them.
//
// FRANK: every export is SELF-CONTAINED — no import, no module-scope constant,
// no bare call to a sibling export (reached through `this.call`), exactly like
// functions/assistantBoard.js. These run as `el.call('name', …)`, so `this` is
// an element.

// ── The SDK seam (SDK-PIN) ───────────────────────────────────────────────────
// @symbo.ls/sdk 3.14.799 forwards `mode` on ai.boards generate / refine and
// marks it: `getService('aiBoards').supportsMode === true` (workspace pin
// 5fbed0373). An OLDER SDK drops `mode` from the request — the server would
// answer a BOARD — so without the marker a Presentation send makes NO model
// call and the deck says it is not available here (the one honest state).
// Returns true | false, or a promise of one (the shell's SDK answers
// getService with a promise on a page's first read).
export const assistantDeckSupport = function assistantDeckSupport(sdk) {
  try {
    const client =
      sdk ||
      (this && this.sdk) ||
      (this && typeof this.call === 'function' ? this.call('getSDK') : null)
    if (!client || client.isNullClient === true || typeof client.getService !== 'function')
      return false
    const has = (svc) => !!(svc && svc.supportsMode === true)
    let svc = null
    try {
      svc = client.getService('aiBoards')
    } catch (_) {
      return false
    }
    if (svc && typeof svc.then === 'function') {
      return Promise.resolve(svc).then(has, () => false)
    }
    return has(svc)
  } catch (_) {
    return false
  }
}

// ── The fixture (the deck contract's example, the Unreal dev twin) ───────────
// Used ONLY by tests and by the `_aiDeckPreview` UI mode (a deck to look at
// before the server's deck path answers). Never a product answer: a preview
// deck cannot be saved. It carries no numbers: every item is a live widget or
// a declarative records block, resolved against THIS workspace's apps — an
// item this workspace cannot mount is counted, like any other.
export const assistantDeckFixture = function assistantDeckFixture() {
  return {
    v: 1,
    kind: 'deck',
    id: 'brd_deck_preview',
    prompt: 'Present our Q3 sales and inventory for the board meeting',
    title: 'Q3 sales and inventory',
    summary: 'Where the pipeline stands, what sold, and what is still available.',
    slides: [
      { id: 's1', kind: 'title', title: 'Q3 sales and inventory', subtitle: 'Board meeting · Q3' },
      {
        id: 's2',
        kind: 'content',
        title: 'The pipeline today',
        subtitle: 'Open deals by stage',
        notes: 'Start with the shape of the pipeline before the sold units.',
        items: [
          {
            id: 's2i1',
            kind: 'widget',
            widget: 'crm.crm-pipeline',
            app: 'crm',
            params: {},
            size: 'l'
          }
        ]
      },
      {
        id: 's3',
        kind: 'content',
        title: 'Deals in negotiation',
        items: [
          {
            id: 's3i1',
            kind: 'records',
            app: 'crm',
            collection: 'crm_deals',
            view: 'stat',
            title: { text: 'Deals in negotiation' },
            filters: [{ field: 'stage', op: 'eq', value: 'offer_negotiation' }],
            aggregate: { op: 'count' },
            size: 's'
          },
          {
            id: 's3i2',
            kind: 'records',
            app: 'crm',
            collection: 'crm_deals',
            view: 'list',
            title: { text: 'The largest ones' },
            filters: [{ field: 'stage', op: 'eq', value: 'offer_negotiation' }],
            sort: { field: 'value', dir: 'desc' },
            limit: 5,
            fields: ['title', 'contactName', 'value', 'currency'],
            size: 'm'
          }
        ]
      },
      {
        id: 's4',
        kind: 'content',
        title: 'What sold this quarter',
        items: [
          {
            id: 's4i1',
            kind: 'records',
            app: 'realestate',
            collection: 're_units',
            view: 'chart',
            title: { text: 'Units sold by space type' },
            filters: [{ field: 'status', op: 'eq', value: 'sold' }],
            period: { field: 'statusChangedAt', preset: 'thisQuarter' },
            aggregate: { op: 'count' },
            groupBy: 'spaceType',
            size: 'l'
          }
        ]
      },
      {
        id: 's5',
        kind: 'content',
        title: 'Viewings this quarter',
        notes: 'Close on demand: the viewings keep the pipeline full.',
        items: [
          {
            id: 's5i1',
            kind: 'records',
            app: 'workspace',
            collection: 'booking',
            view: 'chart',
            title: { text: 'Viewings by status' },
            filters: [{ field: 'kind', op: 'eq', value: 'viewing' }],
            period: { field: 'startAt', preset: 'thisQuarter' },
            aggregate: { op: 'count' },
            groupBy: 'status',
            size: 'l'
          }
        ]
      }
    ],
    sources: [{ app: 'crm' }, { app: 'realestate' }, { app: 'workspace' }],
    generatedAt: null,
    model: 'fixture'
  }
}

// ── Spec → the slides ────────────────────────────────────────────────────────
// Slide 1 is the title slide (made from the deck title when the spec has
// none, like the server does); then at most 8 content slides, each with at
// most 2 items. A content slide's items are the BOARD's rows
// (boardItemsFor): `total` what the slide asked for, `dropped` what this shell
// cannot mount. Texts are capped at the contract's lengths.
export const assistantDeckSlidesFor = function assistantDeckSlidesFor(spec, root) {
  const text = (v, n) => (typeof v === 'string' ? v.trim().slice(0, n) : '')
  const s = spec && typeof spec === 'object' ? spec : {}
  const deckId = text(s.id, 80) || 'deck'
  const list = Array.isArray(s.slides) ? s.slides.filter((x) => x && typeof x === 'object') : []
  const first = list[0] && list[0].kind === 'title' ? list[0] : null
  const titleSlide = {
    key: 's1',
    id: 's1',
    kind: 'title',
    title: text(first ? first.title : s.title, 80) || text(s.title, 80),
    subtitle: text(first ? first.subtitle : s.summary, 140),
    notes: text(first && first.notes, 600),
    items: [],
    total: 0,
    dropped: 0
  }
  const contents = list.filter((x) => x.kind !== 'title').slice(0, 8)
  let total = 0
  let dropped = 0
  const slides = [titleSlide]
  contents.forEach((raw, i) => {
    const id = text(raw.id, 8) || 's' + (i + 2)
    const items = Array.isArray(raw.items) ? raw.items.slice(0, 2) : []
    const derived = this.call(
      'boardItemsFor',
      {
        v: 1,
        id: deckId + '-' + id,
        prompt: text(s.prompt, 500),
        title: text(raw.title, 60),
        items,
        sources: Array.isArray(s.sources) ? s.sources : []
      },
      root
    )
    total += derived.total
    dropped += derived.dropped
    slides.push({
      key: id,
      id,
      kind: 'content',
      title: text(raw.title, 80),
      subtitle: text(raw.subtitle, 140),
      notes: text(raw.notes, 600),
      items: derived.items.map((it) => ({ ...it, slide: id })),
      total: derived.total,
      dropped: derived.dropped
    })
  })
  return { slides, total, dropped }
}

// ── The streaming reducer ────────────────────────────────────────────────────
// Frames as the SDK hands them to `onEvent` — `{ event, data }` — over the
// board frame names (deck contract §3). The raw DeckSpec grows as frames land
// (a slide at its index, validated by the server) and the slides re-derive
// from it after every frame (idempotent: a frame read twice changes nothing);
// `board.done` is AUTHORITATIVE.
export const applyDeckFrame = function applyDeckFrame(deck, frame, root) {
  if (!deck || !frame || typeof frame !== 'object') return deck
  const event = String(frame.event || '')
  const data = frame.data && typeof frame.data === 'object' ? frame.data : {}
  if (event === 'board.error') return this.call('assistantDeckFailed', deck, data.code || 'failed')
  const raw = deck.raw
    ? { ...deck.raw, slides: Array.isArray(deck.raw.slides) ? deck.raw.slides.slice() : [] }
    : { v: 1, kind: 'deck', prompt: deck.prompt, slides: [] }
  let status = deck.status === 'loading' ? 'streaming' : deck.status
  let dropped = Number(deck.streamDropped) || 0
  if (event === 'board.start') {
    if (data.id) raw.id = data.id
  } else if (event === 'board.head') {
    if (typeof data.title === 'string') raw.title = data.title
    if (typeof data.summary === 'string') raw.summary = data.summary
    if (data.id) raw.id = data.id
  } else if (event === 'board.slide') {
    const slide = data.slide && typeof data.slide === 'object' ? data.slide : null
    const at = Number.isInteger(data.index) && data.index >= 0 ? data.index : raw.slides.length
    // Slides land at their INDEX (the title slide may come after a content
    // one): the raw list keeps every position — a hole is null until its
    // slide lands; the derivation skips holes.
    if (slide) {
      raw.slides[at] = slide
      raw.slides = Array.from(raw.slides, (x) => x || null)
    }
  } else if (event === 'board.drop') {
    dropped += 1
  } else if (event === 'board.done') {
    const spec = data.spec && typeof data.spec === 'object' ? data.spec : null
    // A done frame with no slides is a BOARD: a server that does not know the
    // deck shape yet answered the ask as a board — not available here.
    if (!spec || !Array.isArray(spec.slides)) {
      return this.call('assistantDeckFailed', deck, spec ? 'unavailable' : 'failed')
    }
    Object.assign(raw, spec)
    raw.slides = spec.slides.slice()
    status = 'ready'
  } else {
    return deck
  }
  const derived = this.call('assistantDeckSlidesFor', raw, root)
  const count = derived.slides.length
  const index = Math.min(Math.max(Number(deck.index) || 0, 0), Math.max(count - 1, 0))
  return {
    ...deck,
    status,
    raw,
    id: raw.id || deck.id || null,
    title: typeof raw.title === 'string' ? raw.title : deck.title || '',
    summary: typeof raw.summary === 'string' ? raw.summary : deck.summary || '',
    slides: derived.slides,
    total: derived.total,
    dropped: derived.dropped,
    streamDropped: dropped,
    index,
    error: null,
    receivedAt: deck.receivedAt || Date.now()
  }
}

// The failure state: the sentence names the reason ('timeout', 'unavailable',
// 'empty' — no content slide the service could build — or 'failed'); a deck
// that was on screen stays under it (its slides are kept).
export const assistantDeckFailed = function assistantDeckFailed(deck, reason) {
  const code = String(reason || '')
  const why =
    code === 'timeout' || code === 'unavailable'
      ? code
      : code === 'empty_deck' || code === 'empty'
        ? 'empty'
        : code === 'invalid_mode'
          ? 'unavailable'
          : 'failed'
  return { ...(deck || {}), status: 'failed', error: why }
}

// ── The view model the components read ──────────────────────────────────────
// `root._aiDeck` when it belongs to the workspace on screen, else — behind the
// `_aiDeckPreview` UI mode — the fixture deck; else null. Every key is read up
// front so a reactive caller subscribes to all of them whichever branch runs.
export const assistantDeckViewModel = function assistantDeckViewModel(root) {
  const r = root || {}
  const deck = r._aiDeck
  const preview = r._aiDeckPreview === true
  const ws = r.workspace
  const live = (ws && (ws.id || ws._id)) || null
  if (deck && typeof deck === 'object') {
    if (!deck.workspaceId || !live || String(deck.workspaceId) === String(live)) return deck
  }
  if (!preview) return null
  const raw = this.call('assistantDeckFixture')
  const derived = this.call('assistantDeckSlidesFor', raw, r)
  const index = Math.min(Math.max(Number(r._aiDeckPreviewIndex) || 0, 0), derived.slides.length - 1)
  return {
    v: 1,
    status: 'ready',
    requestId: 'preview',
    preview: true,
    path: '/',
    prompt: raw.prompt,
    raw,
    id: raw.id,
    title: raw.title,
    summary: raw.summary,
    slides: derived.slides,
    total: derived.total,
    dropped: derived.dropped,
    index,
    error: null,
    save: null,
    savedId: null,
    notice: null,
    dismissed: false,
    receivedAt: null
  }
}

// The DECK view's gate: a deck that was not dismissed, on the route it was
// made on (a deck with no route — a saved one opened from the row — belongs to
// the home), with the main placement and the board service answering — or an
// EXPLICIT open (a saved deck from the row: shown on the home whatever the
// placement), or the preview on the home. Never while the main thread holds
// the main area (ONE view at a time).
export const assistantDeckShown = function assistantDeckShown(root) {
  const r = root || {}
  const path = r.currentPath
  const dock = r.assistantDock
  const available = r._aiBoardsAvailable === true
  const vm = this.call('assistantDeckViewModel', r)
  const fns = this.context && this.context.functions
  const thread =
    fns && typeof fns.assistantMainThreadOpen === 'function'
      ? !!this.call('assistantMainThreadOpen', r)
      : false
  if (!vm || vm.dismissed) return false
  const norm = (p) => {
    const raw = typeof p === 'string' && p ? p : '/'
    const s = this.call('stripWorkspacePrefix', raw)
    const v = typeof s === 'string' ? s : raw
    return v === '' ? '/' : v.length > 1 ? v.replace(/\/+$/, '') : v
  }
  const route = norm(path)
  if (norm(vm.path) !== route) return false
  if (thread) return false
  if (vm.explicit || vm.preview) return route === '/'
  return dock === 'main' && available
}

// The slide on screen (the view's stage): the deck's slide at its index; while
// nothing has landed yet, a skeleton slide.
export const assistantDeckSlide = function assistantDeckSlide(vm) {
  const slides = vm && Array.isArray(vm.slides) ? vm.slides : []
  if (!slides.length) {
    return vm && (vm.status === 'loading' || vm.status === 'streaming')
      ? { key: 'skeleton', kind: 'skeleton', items: [] }
      : null
  }
  const i = Math.min(Math.max(Number(vm.index) || 0, 0), slides.length - 1)
  return slides[i]
}

// The counter: "3 / 6" — the slide on screen and the slides that landed.
export const assistantDeckCounter = function assistantDeckCounter(vm) {
  const slides = vm && Array.isArray(vm.slides) ? vm.slides : []
  if (!slides.length) return ''
  const i = Math.min(Math.max(Number(vm.index) || 0, 0), slides.length - 1)
  return String(this.call('polyglot', 'workspace.assistantDeck.counter'))
    .replace('{index}', String(i + 1))
    .replace('{count}', String(slides.length))
}

// Can the view move back / forward from the slide on screen?
export const assistantDeckCanGo = function assistantDeckCanGo(vm, step) {
  const slides = vm && Array.isArray(vm.slides) ? vm.slides : []
  const i = Math.min(Math.max(Number(vm && vm.index) || 0, 0), Math.max(slides.length - 1, 0))
  return step < 0 ? i > 0 : i < slides.length - 1
}

// The micro-label: "Presentation · 6 slides · just now" / "Building the deck".
export const assistantDeckMicro = function assistantDeckMicro(vm) {
  if (!vm) return ''
  if (vm.status === 'loading' || vm.status === 'streaming') {
    return this.call('polyglot', 'workspace.assistantDeck.microLoading')
  }
  const count = Array.isArray(vm.slides) ? vm.slides.length : 0
  const at = (vm.raw && vm.raw.generatedAt) || vm.receivedAt || null
  const when = at ? this.call('timeAgo', at) || '' : ''
  return String(this.call('polyglot', 'workspace.assistantDeck.micro'))
    .replace('{count}', String(count))
    .replace('{when}', when)
    .replace(/\s·\s$/, '')
}

// The status line: a failure's one sentence, an empty slide, a save notice.
export const assistantDeckStatus = function assistantDeckStatus(vm) {
  if (!vm) return ''
  if (vm.status === 'failed') {
    if (vm.error === 'timeout') return this.call('polyglot', 'workspace.assistantDeck.timeout')
    if (vm.error === 'unavailable')
      return this.call('polyglot', 'workspace.assistantDeck.unavailable')
    if (vm.error === 'empty') return this.call('polyglot', 'workspace.assistantDeck.empty')
    return this.call('polyglot', 'workspace.assistantDeck.failed')
  }
  if (vm.notice === 'saveFailed') return this.call('polyglot', 'workspace.assistantDeck.saveFailed')
  return ''
}

// The Save button's label index (AppLabelStack: save · saving · saved).
export const assistantDeckSaveLabel = function assistantDeckSaveLabel(vm) {
  const state = vm && vm.save
  return state === 'saving' ? 1 : state === 'saved' ? 2 : 0
}

// Save / Present wait for a ready, real deck (never a preview).
export const assistantDeckActionsIdle = function assistantDeckActionsIdle(vm) {
  return !vm || vm.preview || vm.status !== 'ready' || !(vm.slides || []).length
}

// ── The verbs ────────────────────────────────────────────────────────────────
// Move to a slide: `step` -1 / +1 (Previous / Next, ← / →), or an index.
export const assistantDeckGo = function assistantDeckGo(step, opts) {
  const root = this.state && this.state.root
  if (!root || typeof root.update !== 'function') return false
  const vm = this.call('assistantDeckViewModel', root)
  const slides = vm && Array.isArray(vm.slides) ? vm.slides : []
  if (!slides.length) return false
  const cur = Math.min(Math.max(Number(vm.index) || 0, 0), slides.length - 1)
  const to = opts && opts.to != null ? Number(opts.to) : cur + (Number(step) || 0)
  const next = Math.min(Math.max(Number.isFinite(to) ? to : cur, 0), slides.length - 1)
  if (next === cur) return false
  if (vm.preview) {
    root.update({ _aiDeckPreviewIndex: next }, { preventFetch: true })
    return true
  }
  root.update({ _aiDeck: { ...root._aiDeck, index: next } }, { preventFetch: true })
  return true
}

// Esc / Dismiss: the deck crossfades out, the page (or the home board) is back.
// The deck stays saved if it was; never changes the mode or the placement.
export const assistantDeckDismiss = function assistantDeckDismiss(opts) {
  const root = this.state && this.state.root
  if (!root || typeof root.update !== 'function') return false
  const d = root._aiDeck
  if (d && !d.dismissed) root.update({ _aiDeck: { ...d, dismissed: true } }, { preventFetch: true })
  if (root._aiDeckPreview === true) root.update({ _aiDeckPreview: false }, { preventFetch: true })
  const fns = this.context && this.context.functions
  if (opts && opts.focusComposer && fns && typeof fns.assistantBoardFocus === 'function') {
    this.call('assistantBoardFocus', 'composer')
  }
  return true
}

// A send in Presentation mode (functions/homeAiSend.js → route 'deck'), Try
// again, or a follow-up: called `(prompt, opts)` or `({ prompt, attachments,
// attached })`. The skeleton deck lands in the SAME tick (the first root write
// happens before any network work); the frames fill it. A follow-up while a
// ready deck shows on this route REFINES it (the refine op with the deck on
// screen, or the saved record's id). `requestId` makes every late write of a
// superseded request a no-op.
export const assistantDeckGenerate = async function assistantDeckGenerate(prompt, opts) {
  const el = this
  const root = el && el.state && el.state.root
  const input = prompt && typeof prompt === 'object' ? prompt : { prompt }
  const text = String(input.prompt == null ? '' : input.prompt).trim()
  if (!root || typeof root.update !== 'function' || !text) return null
  const o = { ...(opts || {}) }
  if (input.attached) o.attached = input.attached
  if (input.attachments) o.attachments = input.attachments
  const ws = root.workspace
  const workspaceId = (ws && (ws.id || ws._id)) || null
  const madeOn = typeof root.currentPath === 'string' && root.currentPath ? root.currentPath : '/'
  // A FOLLOW-UP refines the deck on screen (the coordinator's reading of
  // Nika's "Live deck in thread": the next ask shapes the same deck).
  const current = root._aiDeck
  const shown = !!el.call('assistantDeckShown', root)
  const onScreen =
    !o.fresh && shown && current && current.status === 'ready' && !current.preview && current.raw
      ? current
      : null
  const refine =
    o.refine || (onScreen ? { spec: onScreen.raw, boardId: onScreen.savedId || null } : null)
  const op = refine ? 'refine' : 'generate'
  // ONE view holds the main area: a board on screen steps aside.
  const fns = el.context && el.context.functions
  if (fns && typeof fns.assistantBoardDismiss === 'function') el.call('assistantBoardDismiss')
  if (fns && typeof fns.assistantFlyClose === 'function') el.call('assistantFlyClose')
  const requestId = 'deck_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8)
  root.update(
    {
      _aiDeck: {
        v: 1,
        status: 'loading',
        requestId,
        workspaceId,
        path: onScreen ? onScreen.path : madeOn,
        prompt: text,
        op,
        raw: null,
        id: null,
        title: onScreen ? onScreen.title : '',
        summary: '',
        slides: onScreen ? onScreen.slides : [],
        total: 0,
        dropped: 0,
        streamDropped: 0,
        index: 0,
        error: null,
        save: null,
        savedId: null,
        notice: null,
        dismissed: false,
        explicit: false,
        preview: false,
        startedAt: Date.now(),
        receivedAt: null
      }
    },
    { preventFetch: true }
  )
  const isCurrent = () => {
    const d = root._aiDeck
    return !!d && d.requestId === requestId
  }
  const apply = (frame) => {
    if (!isCurrent()) return
    const next = el.call('applyDeckFrame', root._aiDeck, frame, root)
    if (next && isCurrent()) root.update({ _aiDeck: next }, { preventFetch: true })
  }
  const fail = (reason) => {
    if (!isCurrent()) return
    root.update(
      { _aiDeck: el.call('assistantDeckFailed', root._aiDeck, reason) },
      { preventFetch: true }
    )
  }
  const reasonOf = (err) => {
    const msg = String((err && (err.code || err.message)) || err || '')
    if (/timeout|timed out/i.test(msg)) return 'timeout'
    if (/empty_deck/i.test(msg)) return 'empty'
    if (
      /^unavailable$|invalid_mode|unknown entity|does not support op|is not available|not found on service|unknown method/i.test(
        msg
      )
    ) {
      return 'unavailable'
    }
    return 'failed'
  }
  const sdk = el.sdk || (typeof el.call === 'function' ? el.call('getSDK') : null)
  const timeoutMs = Number(o.timeoutMs) > 0 ? Number(o.timeoutMs) : 45000
  let timer = null
  try {
    // SDK-PIN: an SDK that would drop `mode` makes no call (assistantDeckSupport).
    let supported = el.call('assistantDeckSupport', sdk)
    if (supported && typeof supported.then === 'function') supported = await supported
    if (!supported || !sdk || typeof sdk.execute !== 'function') throw new Error('unavailable')
    let capabilities = el.call('assistantClientCapabilities', sdk)
    if (capabilities && typeof capabilities.then === 'function') capabilities = await capabilities
    const pending = sdk.execute('ai.boards', op, {
      prompt: text,
      mode: 'presentation',
      ...(refine && refine.boardId ? { boardId: refine.boardId } : {}),
      ...(refine && !refine.boardId && refine.spec ? { spec: refine.spec } : {}),
      ...(o.threadId ? { threadId: o.threadId } : {}),
      ...(workspaceId ? { workspaceId } : {}),
      ...(o.attached ? { attached: o.attached } : {}),
      ...(o.attachments ? { attachments: o.attachments } : {}),
      ...(capabilities ? { capabilities } : {}),
      onEvent: (frame) => {
        if (frame && typeof frame === 'object' && frame.event !== 'board.done') apply(frame)
      }
    })
    const res = await Promise.race([
      Promise.resolve(pending),
      new Promise((resolve, reject) => {
        timer = setTimeout(() => reject(new Error('timeout')), timeoutMs)
      })
    ])
    const data = res && typeof res === 'object' ? res.data || res : null
    apply({ event: 'board.done', data: data && data.spec ? data : { spec: data } })
  } catch (err) {
    fail(reasonOf(err))
  } finally {
    if (timer) clearTimeout(timer)
  }
  const done = root._aiDeck
  if (!done || done.requestId !== requestId) return null
  if (done.status === 'ready') el.call('assistantDeckFocus', 'title')
  return done
}

// Try again: the last request, the same way.
export const assistantDeckRetry = function assistantDeckRetry() {
  const root = this.state && this.state.root
  const d = root && root._aiDeck
  if (!d || !d.prompt) return null
  return this.call('assistantDeckGenerate', d.prompt, { fresh: d.op !== 'refine' })
}

// Focus after a deck lands: its title (a reading position, never a ring), or
// back to the composer — the DOMQL tree, never a selector.
export const assistantDeckFocus = function assistantDeckFocus(target) {
  try {
    const app = typeof this.getRoot === 'function' ? this.getRoot() : null
    if (target === 'composer') return !!this.call('assistantBoardFocus', 'composer')
    const view =
      app && typeof app.lookdown === 'function' ? app.lookdown('AssistantDeckView') : null
    const title = view && typeof view.lookdown === 'function' ? view.lookdown('DeckTitle') : null
    const node = title && title.node
    if (node && typeof node.focus === 'function' && node.isConnected !== false) {
      node.focus({ preventScroll: true })
      return true
    }
  } catch (_) {
    /* no view on this surface */
  }
  return false
}

// ── Save (the saved boards row, kind 'deck') ─────────────────────────────────
// ONE `ai_boards` record whose spec is the DeckSpec: the server stores it as
// kind 'deck' (deck contract §6). The record joins `_aiBoards` — the saved
// boards row lists it, and a pick opens it as a deck (assistantDeckOpenSaved).
export const saveDeck = async function saveDeck(el, s) {
  const host = el || this
  const root = (s && s.root) || (host && host.state && host.state.root)
  const deck = root && root._aiDeck
  if (!root || !deck || deck.preview || deck.status !== 'ready' || !deck.raw) {
    return { ok: false, reason: 'unavailable' }
  }
  if (deck.save === 'saved' && deck.savedId) return { ok: true, already: true }
  const mark = (save, notice, savedId) => {
    const d = root._aiDeck
    if (d && d.requestId === deck.requestId) {
      root.update(
        { _aiDeck: { ...d, save, notice, ...(savedId ? { savedId } : {}) } },
        { preventFetch: true }
      )
    }
  }
  const ws = root.workspace
  const workspaceId = (ws && (ws.id || ws._id)) || null
  mark('saving', null)
  try {
    const sdk = host.sdk || host.call('getSDK')
    if (!sdk || typeof sdk.execute !== 'function') throw new Error('unknown method: ai.boards')
    const saved = await sdk.execute('ai.boards', 'save', {
      spec: deck.raw,
      title: deck.title || deck.prompt,
      ...(workspaceId ? { workspaceId } : {})
    })
    const row = saved && typeof saved === 'object' ? saved.data || saved : null
    if (row && row.id) {
      const list = Array.isArray(root._aiBoards) ? root._aiBoards : []
      root.update(
        { _aiBoards: [{ kind: 'deck', ...row }, ...list.filter((x) => x && x.id !== row.id)] },
        { preventFetch: true }
      )
    }
    mark('saved', null, row && row.id)
    return { ok: true, board: row }
  } catch (err) {
    mark('failed', 'saveFailed')
    return { ok: false, reason: 'failed', error: (err && err.message) || String(err) }
  }
}

// A saved deck picked from the row (functions/assistantBoard.js
// assistantBoardSelect routes a `kind: 'deck'` record here): the deck shows on
// the HOME whatever the placement — an explicit open, like a board tab.
export const assistantDeckOpenSaved = function assistantDeckOpenSaved(row) {
  const root = this.state && this.state.root
  const spec = row && typeof row === 'object' ? row.spec || null : null
  if (!root || typeof root.update !== 'function' || !spec || !Array.isArray(spec.slides))
    return false
  const fns = this.context && this.context.functions
  if (fns && typeof fns.assistantBoardDismiss === 'function') this.call('assistantBoardDismiss')
  if (fns && typeof fns.assistantMainThreadClose === 'function')
    this.call('assistantMainThreadClose')
  const derived = this.call('assistantDeckSlidesFor', spec, root)
  const ws = root.workspace
  root.update(
    {
      _aiDeck: {
        v: 1,
        status: 'ready',
        requestId: 'saved_' + row.id,
        workspaceId: (ws && (ws.id || ws._id)) || null,
        path: '/',
        prompt: spec.prompt || '',
        op: 'generate',
        raw: spec,
        id: spec.id || row.id,
        title: row.title || spec.title || '',
        summary: spec.summary || '',
        slides: derived.slides,
        total: derived.total,
        dropped: derived.dropped,
        streamDropped: 0,
        index: 0,
        error: null,
        save: 'saved',
        savedId: row.id,
        notice: null,
        dismissed: false,
        explicit: true,
        preview: false,
        startedAt: null,
        receivedAt: Date.now()
      }
    },
    { preventFetch: true }
  )
  return true
}

// THE DECK FOLLOWS THE INSTALLED VOCABULARY (the board's qa round 11 F1): a
// ready deck re-derives its slides when the installed apps publish, and writes
// only when a slide's rows changed.
export const assistantDeckRederive = function assistantDeckRederive(root) {
  const r = root || (this && this.state && this.state.root) || null
  const vm = r && r._aiDeck
  if (!vm || typeof vm !== 'object' || typeof r.update !== 'function') return false
  if (vm.status !== 'ready' || vm.preview || !vm.raw) return false
  const derived = this.call('assistantDeckSlidesFor', vm.raw, r)
  const sig = (slides) =>
    (Array.isArray(slides) ? slides : [])
      .map((sl) => sl.key + ':' + (sl.items || []).map((it) => it.kind + '/' + it.key).join(','))
      .join('|')
  if (sig(derived.slides) === sig(vm.slides)) return false
  r.update(
    { _aiDeck: { ...vm, slides: derived.slides, total: derived.total, dropped: derived.dropped } },
    { preventFetch: true }
  )
  return true
}

// ── Present (a full-viewport layer) ──────────────────────────────────────────
// The deck on screen, full viewport, through the shell's ModalRoot stack — the
// ONE overlay system: Esc closes the top layer (the presenter, then the deck
// view under it), a press on the backdrop too, and the shell's keyboard
// registry stands down while it is up (the presenter moves on ← / → itself).
export const assistantDeckPresent = function assistantDeckPresent() {
  const root = this.state && this.state.root
  const vm = root ? this.call('assistantDeckViewModel', root) : null
  if (!vm || !(vm.slides || []).length) return false
  this.call('openModal', {
    id: 'assistant-deck-presenter',
    template: 'custom',
    width: '100%',
    maxWidth: '100%',
    maxHeight: '100%',
    content: { extends: 'AssistantDeckPresenter' }
  })
  return true
}

export const assistantDeckPresentExit = function assistantDeckPresentExit() {
  this.call('closeModal', 'assistant-deck-presenter')
  return true
}

// The presenter's keys, on its own focused surface (the registry is down under
// a modal): ← / → / PageUp / PageDown / Space move, Home / End jump.
export const assistantDeckPresenterKey = function assistantDeckPresenterKey(e) {
  const key = e && e.key
  const moves = {
    ArrowRight: 1,
    ArrowDown: 1,
    PageDown: 1,
    ' ': 1,
    ArrowLeft: -1,
    ArrowUp: -1,
    PageUp: -1
  }
  const root = this.state && this.state.root
  const vm = root ? this.call('assistantDeckViewModel', root) : null
  const count = vm && Array.isArray(vm.slides) ? vm.slides.length : 0
  let moved = false
  if (moves[key]) moved = this.call('assistantDeckGo', moves[key])
  else if (key === 'Home') moved = this.call('assistantDeckGo', 0, { to: 0 })
  else if (key === 'End') moved = this.call('assistantDeckGo', 0, { to: count - 1 })
  else return false
  if (e && typeof e.preventDefault === 'function') e.preventDefault()
  return moved
}

// ── The keys while the deck view shows (the shell keyboard registry) ────────
// Esc closes the deck view (focus back to the composer); ← / → move. They run
// only while THIS view is on the page and shown, never while a page sheet sits
// above it (its own Esc comes first), and never in a field (the registry's
// guard). Two views register them — the home's and the page layer's
// (`mainHost: 'over'`): each keeps its own ids, the layer's unlabelled.
export const assistantDeckShortcuts = function assistantDeckShortcuts(el) {
  const view = el || this
  if (!view || typeof view.call !== 'function') return () => false
  const over = view.mainHost === 'over'
  view.call('listShortcuts')
  const shown = (h, root) => {
    const node = view.node
    if (!node || node.isConnected === false) return false
    if (root && (root._canvasSlot || root._canvasSlotSecond)) return false
    return !!view.call('assistantDeckShown', root)
  }
  const row = (id, keys, label, order, run) =>
    view.call('registerShortcut', {
      id: over ? id + 'Over' : id,
      keys,
      label: over ? null : label,
      group: 'shell.headerTabs.dashboard',
      groupOrder: 3,
      order,
      when: shown,
      run
    })
  const offs = [
    row('assistantDeckDismiss', 'escape', 'workspace.assistantDeck.shortcutDismiss', 10, () =>
      view.call('assistantDeckDismiss', { focusComposer: true })
    ),
    row('assistantDeckPrevious', 'arrowleft', 'workspace.assistantDeck.shortcutPrevious', 11, () =>
      view.call('assistantDeckGo', -1)
    ),
    row('assistantDeckNext', 'arrowright', 'workspace.assistantDeck.shortcutNext', 12, () =>
      view.call('assistantDeckGo', 1)
    )
  ]
  const off = () => offs.map((fn) => typeof fn === 'function' && fn()).some(Boolean)
  view.__deckShortcutsOff = off
  return off
}
